import * as React from "react"
import { cn } from "../../lib/utils"
import type { InputProps } from "./Input.types"
import { cva } from "class-variance-authority"

export const labelVariants = cva(
  "font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
  {
    variants: {
      size: {
        xs: "text-xs",
        sm: "text-sm",
        base: "text-base",
        l: "text-lg",
        xl: "text-xl",
      },
    },
    defaultVariants: {
      size: "base",
    },
  }
)

const inputVariants = cva(
  "flex w-full rounded-md border border-input bg-transparent transition-colors file:border-0 file:bg-transparent file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-within:ring-1 focus-within:ring-primary-400 focus-within:border-primary-400 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-zinc-100 dark:disabled:bg-zinc-800/80 disabled:text-zinc-500 dark:disabled:text-zinc-400",
  {
    variants: {
      inputSize: {
        xs: "h-7 px-2 py-1 text-xs file:text-xs",
        sm: "h-8 px-2 py-1 text-sm file:text-sm",
        base: "h-9 px-3 py-1.5 text-base file:text-base",
        l: "h-10 px-3 py-2 text-lg file:text-lg",
        xl: "h-11 px-4 py-2 text-xl file:text-xl",
      },
    },
    defaultVariants: {
      inputSize: "base",
    },
  }
)

const iconSizeMap = {
  xs: "w-3 h-3",
  sm: "w-3.5 h-3.5",
  base: "w-4 h-4",
  l: "w-5 h-5",
  xl: "w-6 h-6",
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, description, helperText, errorMessage, id, inputSize = 'base', actionText, ...props }, ref) => {
    const [generatedId] = React.useState(() => Math.random().toString(36).substring(2, 9));
    const inputId = id || `input-${generatedId}`;

    return (
      <div className="grid w-full items-center gap-2">
        {(label || actionText || description) && (
          <div className="flex flex-col gap-2 w-full">
            {(label || actionText) && (
              <div className="flex items-center justify-between w-full">
                {label && (
                  <label htmlFor={inputId} className={cn(labelVariants({ size: inputSize }))}>
                    {label}
                    {props.required && <span className="text-destructive ml-1">*</span>}
                  </label>
                )}
                {actionText && (
                  <a href="#" className="text-xs font-medium text-primary dark:text-primary-300 cursor-pointer hover:underline">
                    {actionText}
                  </a>
                )}
              </div>
            )}
            {description && (
              <p className={cn(inputSize === 'xs' ? "text-xs" : "text-sm", props.error ? "text-destructive" : "text-muted-foreground")}>
                {description}
              </p>
            )}
          </div>
        )}
        <div className="relative w-full">
          {props.startIcon && (
            <div className={cn("absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center", props.error ? "text-destructive" : "text-muted-foreground", iconSizeMap[inputSize])}>
              {React.cloneElement(props.startIcon as React.ReactElement<any>, {
                className: cn((props.startIcon as React.ReactElement<any>).props.className, iconSizeMap[inputSize])
              })}
            </div>
          )}
          <input
            id={inputId}
            type={type}
            className={cn(
              inputVariants({ inputSize, className }),
              props.startIcon && "pl-10",
              props.endIcon && "pr-10",
              props.error && "border-destructive ring-1 ring-destructive focus-within:ring-destructive focus-within:border-destructive text-destructive placeholder:text-destructive/60",
              props.readOnly && "bg-zinc-100 dark:bg-zinc-800/80 text-zinc-500 dark:text-zinc-400 focus-visible:ring-0 focus-visible:border-input focus-visible:ring-offset-0 cursor-default",
              type === "date" || type === "time" || type === "datetime-local" || type === "month" || type === "week" ? "block" : ""
            )}
            ref={ref}
            {...props}
          />
          {props.endIcon && (
            <div className={cn("absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center", props.error ? "text-destructive" : "text-muted-foreground", iconSizeMap[inputSize])}>
              {React.cloneElement(props.endIcon as React.ReactElement<any>, {
                className: cn((props.endIcon as React.ReactElement<any>).props.className, iconSizeMap[inputSize])
              })}
            </div>
          )}
        </div>
        {(helperText || errorMessage) && (
          <p className={cn(inputSize === 'xs' ? "text-xs" : "text-sm", props.error ? "text-destructive" : "text-muted-foreground")}>
            {props.error && errorMessage ? errorMessage : helperText}
          </p>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
